import * as React from "react";
import mergePluginBindings from "util/mergePluginBindings";
import {
  CreateInvestigationNotificationDetails,
  CreateInvestigationNotificationForm,
  CreateInvestigationNotificationSummary
} from "security-app/components/Investigations/notifications";
import { PaginationProvider } from "common/contexts";
import useLicenseCheck from "license/useLicenseCheck";
import SecurityPage from "security-app/SecurityPage";
import useCurrentUser from "hooks/useCurrentUser";
import { isPermitted } from "util/PermissionsMixin";
import entityRoutesResolver from "security-app/routes/entityRoutesResolver";
import AssetsField from "security-app/components/Assets/plugin/AssetsField";
import useAssetKeyMapper from "security-app/components/Assets/plugin/useAssetKeyMapper";
import ExportResultsButton from "security-app/components/SecurityEvents/Alerts/ExportResultsButton";
import securityEntityPermissionsMapper from "security-app/SecurityEntityPermissionsMapper";
import AssetEventFormContainer from "security-app/components/AssetEvents/AssetEventFormContainer";
import AssetEventForm from "security-app/components/AssetEvents/AssetEventForm";
import AssetEventSummaryContainer from "security-app/components/AssetEvents/AssetEventSummaryContainer";
import AnomalyEventFormContainer from "security-app/components/AnomalyEvents/AnomalyEventFormContainer";
import AnomalyEventForm from "security-app/components/AnomalyEvents/AnomalyEventForm";
import AnomalyEventSummaryContainer from "security-app/components/AnomalyEvents/AnomalyEventSummaryContainer";
import FilterAggregationFormContainer from "components/event-definitions/event-definition-types/FilterAggregationFormContainer";
import FilterAggregationForm from "components/event-definitions/event-definition-types/FilterAggregationForm";
import FilterAggregationSummary from "components/event-definitions/event-definition-types/FilterAggregationSummary";
import CorrelationForm from "event-correlation/CorrelationForm";
import SigmaCorrelationFormContainer from "security-app/components/SigmaRules/SigmaCorrelation/SigmaCorrelationFormContainer";
import SigmaCorrelationSummaryContainer from "security-app/components/SigmaRules/SigmaCorrelation/SigmaCorrelationSummaryContainer";
import anomalyPageNavigation from "security-app/components/Layout/anomalyPageNavigation";
import assetsPageNavigation from "security-app/components/Layout/assetsPageNavigation";
import investigationsPageNavigation from "security-app/components/Layout/investigationsPageNavigation";
import SecurityRoutes from "security-app/routing/Routes";
import AssetSidebarContent from "security-app/components/Assets/Sidebar/AssetSidebarContent";
import EventReplaySideBarDetails from "security-app/components/SecurityEvents/ReplaySearch/EventReplaySideBarDetails";
import EventDefinitionSideBarDetails from "security-app/components/SecurityEvents/ReplaySearch/EventDefinitionSideBarDetails";
import InvestigationSidebarContent from "security-app/components/Investigations/Sidebar/InvestigationSidebarContent";
import TourOverlay from "./components/VerascendTour/TourOverlay";
import TourRestartNavItem from "./components/VerascendTour/TourRestartNavItem";
import VerascendTourConfig from "./components/VerascendTour/VerascendTourConfig";
import investigationsPlugin from "./components/Investigations/plugin";
import assetsPlugin from "./components/Assets/plugin";
import sigmaPlugin from "./components/SigmaRules/plugin";
import eventProceduresPlugin from "./components/SecurityEvents/EventProcedures/plugin";
import securityEventsPlugin from "./components/SecurityEvents/Alerts/plugin";
import welcomePageMetricsPlugin from "./components/WelcomeSecurityMetrics/plugin";
import TacticsTechniquesEditorPlugin from "./components/EventDefinitions/TacticsTechniquesEditorPlugin";
import TacticsTechniquesColumnCell from "./components/EventDefinitions/TacticsTechniquesColumnCell";
import EventTacticsTechniquesDetailRow from "./components/EventDefinitions/EventTacticsTechniquesDetailRow";
import TacticsTechniquesSummary from "./components/EventDefinitions/TacticsTechniquesSummary";
import { InvestigationDrawerProvider } from "./components/Investigations/contexts";
import InvestigationDrawerRenderer from "./components/Investigations/contexts/InvestigationDrawerRenderer";
const createInvestigationNotificationDefaultConfig = {
  create_new_investigation: false,
  investigation_assigned_to: null,
  investigation_id: null,
  investigation_priority: null,
  investigation_status: null,
  type: "create-investigation-notification-v1"
};
const pluginExports = mergePluginBindings(
  {
    entityPermissionsMapper: securityEntityPermissionsMapper,
    entityRoutes: [entityRoutesResolver],
    globalContextProviders: [InvestigationDrawerProvider],
    pageContextProviders: [InvestigationDrawerRenderer, PaginationProvider],
    "eventDefinitions.components.tacticsTechniquesEditor": [{ component: TacticsTechniquesEditorPlugin }],
    "eventDefinitions.components.tacticsTechniquesColumn": [
      {
        attribute: { id: "tactics_techniques", title: "Tactics/Techniques", sortable: false },
        component: TacticsTechniquesColumnCell,
        useCondition: () => {
          const {
            security: { isValid: isValidSecurityLicense }
          } = useLicenseCheck();
          return isValidSecurityLicense;
        }
      }
    ],
    "events.components.tacticsTechniquesDetailRow": [
      {
        component: EventTacticsTechniquesDetailRow,
        useCondition: () => {
          const {
            security: { isValid: isValidSecurityLicense }
          } = useLicenseCheck();
          return isValidSecurityLicense;
        }
      }
    ],
    "eventDefinitions.components.tacticsTechniquesSummary": [
      {
        component: TacticsTechniquesSummary,
        useCondition: () => {
          const {
            security: { isValid: isValidSecurityLicense }
          } = useLicenseCheck();
          return isValidSecurityLicense;
        }
      }
    ],
    eventNotificationTypes: [
      {
        type: "create-investigation-notification-v1",
        displayName: "Create Investigation Notification [Security]",
        formComponent: CreateInvestigationNotificationForm,
        summaryComponent: CreateInvestigationNotificationSummary,
        detailsComponent: CreateInvestigationNotificationDetails,
        defaultConfig: createInvestigationNotificationDefaultConfig
      }
    ],
    eventDefinitionTypes: [
      {
        type: "asset-v1",
        displayName: "Asset Events",
        sortOrder: 3,
        // Filter & Aggregation, Event Correlation, then sort alphabetically.
        description: "Create Events based on Assets.",
        formComponent: AssetEventFormContainer,
        summaryComponent: AssetEventSummaryContainer,
        defaultConfig: AssetEventForm.defaultConfig,
        useCondition: () => {
          const {
            security: { isValid: isValidSecurityLicense }
          } = useLicenseCheck();
          const { permissions } = useCurrentUser();
          return isPermitted(permissions, "asset:read") && isValidSecurityLicense;
        }
      },
      {
        type: "native-anomaly-v1",
        displayName: "Anomaly Events",
        sortOrder: 2,
        // Filter & Aggregation, Event Correlation, then sort alphabetically.
        description: "Detect anomalies using machine learning algorithms.",
        formComponent: AnomalyEventFormContainer,
        summaryComponent: AnomalyEventSummaryContainer,
        defaultConfig: AnomalyEventForm.defaultConfig,
        useCondition: () => {
          const {
            security: { isValid: isValidSecurityLicense },
            enterprise: { isValid: isValidEnterpriseLicense }
          } = useLicenseCheck();
          return isValidSecurityLicense || isValidEnterpriseLicense;
        }
      },
      {
        type: "sigma-v1",
        displayName: "Sigma Rule",
        sortOrder: 4,
        description: "Detect threats using Sigma rules.",
        formComponent: FilterAggregationFormContainer,
        summaryComponent: FilterAggregationSummary,
        defaultConfig: {
          ...FilterAggregationForm.defaultConfig,
          type: "sigma-v1"
        },
        // Sigma defs are only created via the rule-import path, so hide the type from the wizard.
        useCondition: () => true,
        hideFromCreation: true
      },
      {
        type: "sigma-correlation-v1",
        displayName: "Sigma Correlation Rule",
        sortOrder: 5,
        description: "Correlate Sigma rule Events to identify meaningful incidents.",
        formComponent: SigmaCorrelationFormContainer,
        summaryComponent: SigmaCorrelationSummaryContainer,
        defaultConfig: {
          ...CorrelationForm.defaultConfig,
          type: "sigma-correlation-v1"
        },
        // Produced only by the 7.2 Sigma migration from temporal_ordered rules, so hide the type
        // from the wizard the same way sigma-v1 does.
        useCondition: () => true,
        hideFromCreation: true
      }
    ],
    fieldTypeValueRenderer: [
      {
        type: "associated-assets",
        render: (value) => /* @__PURE__ */ React.createElement(AssetsField, { value })
      }
    ],
    visualizationKeyMappers: [{ type: "associated-assets", useKeyMapper: useAssetKeyMapper }],
    "events.metrics.actions": [
      { id: "export-widget", component: (props) => /* @__PURE__ */ React.createElement(ExportResultsButton, { type: "alerts", title: "alerts", ...props }) }
    ],
    pageNavigation: [anomalyPageNavigation, assetsPageNavigation, investigationsPageNavigation],
    securityPage: [SecurityPage],
    entityTypeRoute: [
      {
        type: "asset",
        route: (id) => SecurityRoutes.ASSETS.details(void 0, id)
      },
      {
        type: "investigation",
        route: (id) => SecurityRoutes.INVESTIGATIONS.details(id)
      }
    ],
    "sidebar.components": [
      { key: "AssetSidebarContent", component: AssetSidebarContent },
      { key: "EventReplaySideBarDetails", component: EventReplaySideBarDetails },
      { key: "EventDefinitionSideBarDetails", component: EventDefinitionSideBarDetails },
      { key: "InvestigationSidebarContent", component: InvestigationSidebarContent }
    ],
    // Verascend first-login product tour, gated by an admin-editable
    // VerascendTourConfig (System / Configurations → "Product Tour"), not a
    // feature flag — see useVerascendTourConfig.ts's own doc comment for why
    // that changed. Both components read that config themselves, so
    // registering them unconditionally here is safe — confirmed pattern via
    // license/bindings.ts's globalNotifications example.
    globalNotifications: [{ key: "org.graylog.plugins.security.VerascendTourOverlay", component: TourOverlay }],
    navigationItems: [
      { key: "org.graylog.plugins.security.VerascendTourRestart", component: TourRestartNavItem }
    ],
    // Registered under the same `systemConfigurations` plugin key every other
    // enterprise plugin config uses (e.g. traffic-violation/bindings.tsx) —
    // confirmed this renders nested under the "Plugins" entry in System /
    // Configurations' sidebar, not as its own top-level sidebar item (that
    // placement, `coreSystemConfigurations`, is reserved for core
    // graylog2-web-interface code — an enterprise plugin registering into it
    // would be non-idiomatic and inconsistent with every other plugin's own
    // config entry).
    systemConfigurations: [
      {
        component: VerascendTourConfig,
        configType: "org.graylog.plugins.securityapp.tour.VerascendTourConfig",
        displayName: "Product Tour",
        permissions: "clusterconfigentry:read"
      }
    ]
  },
  investigationsPlugin,
  assetsPlugin,
  sigmaPlugin,
  eventProceduresPlugin,
  securityEventsPlugin,
  welcomePageMetricsPlugin
);
export default pluginExports;
